/**
 * GOOGLE ADS WORKSPACE — the campaign → ad group → ad tree shared by
 * /google-ads/campaigns (every channel type) and /google-ads/youtube
 * (VIDEO / DEMAND_GEN only). One component so the two pages cannot drift.
 *
 * Structure mirrors WhisperCRM's YouTube workspace: toolbar card (customer chip,
 * list/grid toggle, Refresh / Disconnect / New campaign), three stat tiles, one
 * rounded card per campaign that expands inline into ad groups and then ads,
 * and a muted "drafts never reached Google" drawer when local debris exists.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, ChevronDown, ChevronRight, Globe, Layers, LayoutGrid, LayoutTemplate, List, MonitorPlay,
  Copy, Plus, RefreshCw, Search, ShoppingBag, Smartphone, Sparkles, ExternalLink, ArrowUpDown, BarChart3, type LucideIcon,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import {
  Users as ObjUsers, MousePointerClick as ObjClick, MonitorPlay as ObjVideo, Sparkles as ObjAware,
  Tag as ObjSales, Smartphone as ObjApp, MapPin as ObjLocal, Store as ObjShop,
  Megaphone as StatCampaigns, Play as StatActive, Pause as StatPaused, Layers as CountGroups, FileText as CountAds,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { ReuseAdDialog } from './ReuseAdDialog';
import { GoogleAdsNotice } from './GoogleAdsShell';
import { AttachCustomerDialog } from '@/components/google-ads/AttachCustomerDialog';
import {
  googleAdsService, GoogleAdsAdGroupRow, GoogleAdsAdRow, GoogleAdsAssetGroupRow, GoogleAdsAssetGroupAsset, GoogleAdsCampaignSummary,
} from '@/services/google-ads.service';
import {
  adGroupAdResourceName, alreadyToasted, approvalOf, biddingLabel, CampaignDates, errMsg, formatMicros, newestFirst, Pill, pretty, Spinner,
  statusOf, Switch, useYoutubeAuth, VideoPlayer, VideoThumb, type YtAuthStatus,
} from '@/components/google-ads/youtube/youtubeShared';

// ── config ───────────────────────────────────────────────────────────────

export interface GoogleAdsWorkspaceProps {
  title: string;
  breadcrumb: string;
  description?: string;
  /** Rendered inside a TabbedPage shell — the shell owns the Topbar. */
  embedded?: boolean;
  /** Which campaigns this page shows (default: everything). */
  filter?: (c: GoogleAdsCampaignSummary) => boolean;
  /** Where "+ New campaign" goes. */
  newCampaignRoute: string;
  /** Icon + copy for the empty state. */
  emptyIcon: LucideIcon;
  emptyHint: string;
  /** localStorage key for the list/grid preference. */
  viewStorageKey: string;
  /** Replaces the default "connect / attach a customer" gate (YouTube has its own card). */
  connectSlot?: (auth: YtAuthStatus, accountUid: string, onChanged: () => void) => React.ReactNode;
  /** Shown above the toolbar (e.g. the developer-token warning). */
  banner?: React.ReactNode;
}

/** Live = it exists on Google. Everything else is local debris. */
const isLive = (r: { id?: string | null; adId?: string | null }) => !!(r.id ?? r.adId);

const adName = (ad: GoogleAdsAdRow) => ad.name || ad.headlines[0] || ad.longHeadlines?.[0] || `Ad ${ad.adId}`;

const isPmax = (c: GoogleAdsCampaignSummary) => c.advertisingChannelType === 'PERFORMANCE_MAX';

/** Channel type → icon tile. Video gets the YouTube-style red tile; the rest use SlightorCRM tokens. */
function channelVisual(type?: string | null): { Icon: LucideIcon; tile: string; label: string } {
  switch (type) {
    case 'VIDEO':
    case 'DEMAND_GEN':
      return { Icon: MonitorPlay, tile: 'bg-danger-soft text-danger', label: type === 'VIDEO' ? 'Video' : 'Demand Gen' };
    case 'SEARCH':
      return { Icon: Search, tile: 'bg-info-soft text-info', label: 'Search' };
    case 'DISPLAY':
      return { Icon: LayoutTemplate, tile: 'bg-primary-soft text-primary', label: 'Display' };
    case 'PERFORMANCE_MAX':
      return { Icon: Sparkles, tile: 'bg-primary-soft text-primary', label: 'Performance Max' };
    case 'SHOPPING':
      return { Icon: ShoppingBag, tile: 'bg-success-soft text-success', label: 'Shopping' };
    case 'MULTI_CHANNEL':
      return { Icon: Smartphone, tile: 'bg-info-soft text-info', label: 'App' };
    default:
      return { Icon: Globe, tile: 'bg-surface-alt text-text-muted', label: pretty(type) || 'Campaign' };
  }
}

/**
 * Google keeps no "objective" on a campaign — it is a UI notion derived from
 * channel type, sub-type and bidding, which is how Google's own list shows it.
 * The keys match the objective picker so the filter and the wizard agree.
 */
type ObjectiveKey = 'leads' | 'traffic' | 'youtube' | 'awareness' | 'sales' | 'app' | 'local' | 'shopping';
const OBJECTIVES: Record<ObjectiveKey, { label: string; Icon: LucideIcon }> = {
  leads:     { label: 'Leads',          Icon: ObjUsers },
  traffic:   { label: 'Traffic',        Icon: ObjClick },
  youtube:   { label: 'YouTube',        Icon: ObjVideo },
  awareness: { label: 'Awareness',      Icon: ObjAware },
  sales:     { label: 'Sales',          Icon: ObjSales },
  app:       { label: 'App promotion',  Icon: ObjApp },
  local:     { label: 'Local',          Icon: ObjLocal },
  shopping:  { label: 'Shopping',       Icon: ObjShop },
};
function objectiveOf(c: GoogleAdsCampaignSummary): ObjectiveKey {
  const ch = c.advertisingChannelType ?? '';
  const sub = c.advertisingChannelSubType ?? '';
  const bid = c.biddingStrategyType ?? '';
  if (ch === 'MULTI_CHANNEL' || sub.includes('APP')) return 'app';
  if (ch === 'SHOPPING') return 'shopping';
  if (ch === 'VIDEO' || ch === 'DEMAND_GEN') return 'youtube';
  if (ch === 'PERFORMANCE_MAX') return sub.includes('LOCAL') || c.name.toLowerCase().includes('local') ? 'local' : 'sales';
  if (/CPM|IMPRESSION_SHARE/.test(bid)) return 'awareness';
  if (/CONVERSION|TARGET_CPA|TARGET_ROAS/.test(bid)) return ch === 'DISPLAY' ? 'sales' : 'leads';
  return 'traffic';
}
const ObjectivePill = ({ k, className }: { k: ObjectiveKey; className?: string }) => {
  const o = OBJECTIVES[k];
  return (
    <span className={clsx('inline-flex shrink-0 items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-medium text-text-secondary', className)}>
      <o.Icon size={10} /> {o.label}
    </span>
  );
};

/**
 * Builder routes per channel. SEARCH campaigns carrying a lead form use the
 * Leads builders (they attach the form); PMAX always builds asset groups;
 * Demand Gen uses the YouTube builders. Standard Video (read-only through the
 * API) and App campaigns have no CRM builder for children → null.
 */
function childRoutes(c: GoogleAdsCampaignSummary, leadFormIds: Set<string>): {
  addChild: ((campaignId: string) => string) | null; addAd: ((adGroupId: string) => string) | null;
} {
  switch (c.advertisingChannelType) {
    case 'VIDEO':
      return { addChild: null, addAd: null };
    case 'SEARCH': {
      const base = leadFormIds.has(c.id) ? '/google-ads/leads' : '/google-ads/traffic';
      return { addChild: (id) => `${base}/create/adgroup?campaign_id=${id}`, addAd: (id) => `${base}/create/ad?adgroup_id=${id}` };
    }
    case 'DISPLAY':
      return {
        addChild: (id) => `/google-ads/awareness/create/adgroup?campaign_id=${id}`,
        addAd: (id) => `/google-ads/awareness/create/ad?adgroup_id=${id}`,
      };
    case 'PERFORMANCE_MAX':
      return { addChild: (id) => `/google-ads/sales/create/assetgroup?campaign_id=${id}`, addAd: null };
    case 'SHOPPING':
      return {
        addChild: (id) => `/google-ads/shopping/create/adgroup?campaign_id=${id}`,
        addAd: (id) => `/google-ads/shopping/create/ad?adgroup_id=${id}`,
      };
    case 'DEMAND_GEN':
      return {
        addChild: (id) => `/google-ads/youtube/create/adgroup?campaign_id=${id}`,
        addAd: (id) => `/google-ads/youtube/create/ad?adgroup_id=${id}`,
      };
    default:
      return { addChild: null, addAd: null };
  }
}

/** "bidding · currency budget / day" — the WhisperCRM sub-line. */
function campaignSubline(c: GoogleAdsCampaignSummary, currency?: string | null): string {
  const parts: string[] = [];
  const bid = biddingLabel(c.biddingStrategyType);
  if (bid) parts.push(bid);
  const daily = formatMicros(c.budgetAmountMicros, currency);
  const total = !daily ? formatMicros(c.budgetTotalAmountMicros, currency) : null;
  if (daily) parts.push(`${daily} / day`);
  else if (total) parts.push(`${total} total`);
  if (parts.length === 0) parts.push(channelVisual(c.advertisingChannelType).label);
  return parts.join(' · ');
}

const SkeletonRows = ({ count = 3, indent = '' }: { count?: number; indent?: string }) => (
  <div className={clsx('space-y-2 py-2', indent)}>
    {Array.from({ length: count }, (_, i) => <div key={i} className="skeleton h-10 rounded-xl" />)}
  </div>
);

// ── ads ──────────────────────────────────────────────────────────────────

function AdRows({ accountUid, customerId, adGroupId, onPlay, readOnly = false, parentPaused = false }: {
  accountUid: string; customerId: string; adGroupId: string; onPlay: (v: string, t: string) => void; readOnly?: boolean; parentPaused?: boolean;
}) {
  const [ads, setAds] = useState<GoogleAdsAdRow[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try { setAds((await googleAdsService.listAds(accountUid, adGroupId)).filter(isLive)); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, adGroupId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (ad: GoogleAdsAdRow) => {
    setBusy(ad.adId);
    const rn = adGroupAdResourceName(customerId, ad.adGroupId, ad.adId);
    try {
      if (ad.status === 'ENABLED') await googleAdsService.pauseAd(accountUid, rn);
      else await googleAdsService.resumeAd(accountUid, rn);
      load();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  if (error) return <GoogleAdsNotice error title="Could not load ads">{error}<Button size="sm" variant="ghost" onClick={load}>Retry</Button></GoogleAdsNotice>;
  if (ads === null) return <SkeletonRows count={2} indent="pl-12" />;
  if (ads.length === 0) return <div className="py-3 pl-9 text-sm text-text-muted">No ads yet.</div>;

  return (
    <div className="pl-9">
      {ads.map((ad) => {
        const st = ad.status === 'ENABLED' && parentPaused ? { label: 'Parent paused', tone: 'paused' as const } : statusOf(ad.status);
        const approval = approvalOf(ad.approvalStatus, ad.reviewStatus);
        const videoId = ad.youtubeVideoId ?? null;
        const sub = [
          videoId ? `youtu.be/${videoId}` : (ad.finalUrls[0] ?? null),
          ad.businessName ?? null,
          ad.type ? pretty(ad.type) : null,
          ad.youtubeVideoIds && ad.youtubeVideoIds.length > 1 ? `${ad.youtubeVideoIds.length} videos` : null,
        ].filter(Boolean).join(' · ') || '—';
        return (
          <div key={ad.adId} className="flex items-center gap-3.5 border-t border-border/40 py-3 first:border-t-0">
            <VideoThumb videoId={videoId} onPlay={() => videoId && onPlay(videoId, adName(ad))} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{adName(ad)}</div>
              <div className="mt-0.5 truncate text-xs text-text-muted">{sub}</div>
            </div>
            {approval && <Pill {...approval} className="hidden sm:inline-flex" />}
            <Pill {...st} />
            <Switch checked={ad.status === 'ENABLED'} disabled={readOnly || ad.type === 'APP_AD' || busy === ad.adId || ad.status === 'REMOVED'} label="Enable ad"
              onCheckedChange={() => toggle(ad)} />
          </div>
        );
      })}
    </div>
  );
}

// ── ad groups ────────────────────────────────────────────────────────────

function AdGroupRows({ accountUid, customerId, campaign, currency, addAd, onPlay, adCounts, onChanged }: {
  accountUid: string; customerId: string; campaign: GoogleAdsCampaignSummary; currency?: string | null;
  addAd: ((adGroupId: string) => string) | null; onPlay: (v: string, t: string) => void; adCounts?: Record<string, number>; onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [error, setError] = useState('');
  const [reuseGroup, setReuseGroup] = useState<GoogleAdsAdGroupRow | null>(null);
  const [newAppGroup, setNewAppGroup] = useState(false);
  const [appGroupName, setAppGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [adsRevision, setAdsRevision] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try { setGroups((await googleAdsService.listAdGroups(accountUid, campaign.id)).filter(isLive)); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAdGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.pauseAdGroup(accountUid, g.id);
      else await googleAdsService.resumeAdGroup(accountUid, g.id);
      load();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  if (error) return <GoogleAdsNotice error title="Could not load ad groups">{error}<Button size="sm" variant="ghost" onClick={load}>Retry</Button></GoogleAdsNotice>;
  if (groups === null) return <SkeletonRows count={2} indent="pl-8" />;

  return (
    <div className="space-y-1 pl-2">
      {campaign.advertisingChannelType === 'MULTI_CHANNEL' && <div className="flex justify-end pb-2"><Button size="sm" variant="ghost" iconLeft={<Plus size={13} />} onClick={() => { setAppGroupName(''); setNewAppGroup(true); }}>New group to reuse an ad</Button></div>}
      <Modal open={newAppGroup} onClose={() => { if (!creatingGroup) setNewAppGroup(false); }} title="New App ad group" widthClass="max-w-md">
        <p className="mb-3 text-sm text-text-secondary">Create a paused group, then choose an existing ad for the same app. Google allows one App ad per group.</p>
        <Input aria-label="Ad group name" placeholder="Ad group name" value={appGroupName} onChange={(e) => setAppGroupName(e.target.value)} />
        <div className="mt-4 flex justify-end gap-2"><Button variant="secondary" disabled={creatingGroup} onClick={() => setNewAppGroup(false)}>Cancel</Button><Button loading={creatingGroup} disabled={!appGroupName.trim()} onClick={async () => {
          setCreatingGroup(true);
          try {
            const result = await googleAdsService.appPromotion.createAdGroup(accountUid, campaign.id, { name: appGroupName.trim(), status: 'PAUSED' });
            await load(); onChanged(); setNewAppGroup(false); setReuseGroup({ id: result.adGroupId, name: appGroupName.trim() } as GoogleAdsAdGroupRow);
          } catch (e) { if (!alreadyToasted(e)) toast.error(errMsg(e)); } finally { setCreatingGroup(false); }
        }}>Create paused group</Button></div>
      </Modal>
      {reuseGroup && <ReuseAdDialog accountUid={accountUid} group={reuseGroup} onClose={() => setReuseGroup(null)} onSaved={() => { setOpenId(reuseGroup.id); setAdsRevision((v) => v + 1); setReuseGroup(null); onChanged(); }} />}
      {groups.length === 0 && <div className="py-2 text-sm text-text-muted">No ad groups yet.</div>}
      {groups.map((g) => {
        const open = openId === g.id;
        const st = statusOf(g.status, g.primaryStatus);
        // Video groups carry their CPV bid under both manual and target CPV. Automated CPC bidding leaves a
        // placeholder CPC (0.01) on the group, so that one is only meaningful under manual CPC.
        const cpv = Number(g.cpvBidMicros) > 0 ? formatMicros(g.cpvBidMicros, currency) : null;
        const cpc = !cpv && (campaign.biddingStrategyType ?? '').startsWith('MANUAL_CPC') ? formatMicros(g.cpcBidMicros, currency) : null;
        return (
          <div key={g.id} className={clsx('rounded-xl border transition-colors', open ? 'border-border-strong bg-surface' : 'border-border/70 bg-surface hover:border-border-strong')}>
            <div className="flex items-center gap-2 px-3 py-2">
              <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left"
                onClick={() => setOpenId(open ? null : g.id)}>
                {open ? <ChevronDown size={16} className="shrink-0 text-text-muted" />
                      : <ChevronRight size={16} className="shrink-0 text-text-muted" />}
                <Layers size={16} className="shrink-0 text-text-muted/70" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-text-primary">{g.name}</div>
                  <div className="truncate text-xs text-text-muted">
                    {g.type ? pretty(g.type) : 'Ad group'}
                    {cpv ? ` · ${cpv} CPV` : cpc ? ` · max CPC ${cpc}` : ''}
                    {adCounts && ` · ${adCounts[g.id] ?? 0} ad${(adCounts[g.id] ?? 0) === 1 ? '' : 's'}`}
                  </div>
                </div>
              </button>
              {['SEARCH', 'DISPLAY', 'MULTI_CHANNEL', 'DEMAND_GEN'].includes(campaign.advertisingChannelType ?? '') && g.status !== 'REMOVED' && <Button variant="ghost" size="sm" disabled={campaign.advertisingChannelType === 'MULTI_CHANNEL' && (adCounts?.[g.id] ?? 0) > 0} title={campaign.advertisingChannelType === 'MULTI_CHANNEL' && (adCounts?.[g.id] ?? 0) > 0 ? 'App groups allow one ad. Create a new group to reuse an ad.' : 'Copy a compatible ad into this group'} onClick={() => setReuseGroup(g)} iconLeft={<Copy size={13} />}>Use existing ad</Button>}
              {addAd && (
                <Button variant="ghost" size="sm" className="h-8 gap-1 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                  onClick={() => navigate(addAd(g.id))}>
                  <Plus size={13} /> Ad
                </Button>
              )}
              <Pill {...st} />
              <Switch checked={g.status === 'ENABLED'} disabled={campaign.advertisingChannelType === 'VIDEO' || busy === g.id || g.status === 'REMOVED'} label="Enable ad group"
                onCheckedChange={() => toggle(g)} />
            </div>
            {open && (
              <div className="border-t border-border/50 px-3.5 pb-2">
                <AdRows parentPaused={campaign.status !== 'ENABLED' || g.status !== 'ENABLED'} key={`${g.id}-${adsRevision}`} readOnly={campaign.advertisingChannelType === 'VIDEO'} accountUid={accountUid} customerId={customerId} adGroupId={g.id} onPlay={onPlay} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** PMAX: asset groups stand in for ad groups + ads. Expanding one lists its assets. */
function AssetGroupRows({ accountUid, campaign }: { accountUid: string; campaign: GoogleAdsCampaignSummary }) {
  const [groups, setGroups] = useState<GoogleAdsAssetGroupRow[] | null>(null);
  const [assets, setAssets] = useState<Record<string, GoogleAdsAssetGroupAsset[] | null>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setError('');
    try {
      const gs = await googleAdsService.performanceMax.listAssetGroups(accountUid, campaign.id);
      setGroups(gs);
      // Fetch each group's assets up front so the sub-line can say "5 headlines · 4 images" without a click.
      gs.forEach((g) => {
        googleAdsService.performanceMax.listAssetGroupAssets(accountUid, g.id)
          .then((a) => setAssets((prev) => ({ ...prev, [g.id]: a })))
          .catch(() => setAssets((prev) => ({ ...prev, [g.id]: [] })));
      });
    } catch (e) { setError(errMsg(e)); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAssetGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.performanceMax.pauseAssetGroup(accountUid, g.id);
      else await googleAdsService.performanceMax.resumeAssetGroup(accountUid, g.id);
      load();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  const summarise = (list: GoogleAdsAssetGroupAsset[] | null | undefined) => {
    if (!list) return 'loading assets…';
    const n = (pred: (a: GoogleAdsAssetGroupAsset) => boolean) => list.filter(pred).length;
    const parts = [
      [n((a) => /HEADLINE/.test(a.fieldType)), 'headline'],
      [n((a) => /DESCRIPTION/.test(a.fieldType)), 'description'],
      [n((a) => a.type === 'IMAGE' && !/LOGO/.test(a.fieldType)), 'image'],
      [n((a) => /LOGO/.test(a.fieldType)), 'logo'],
      [n((a) => a.type === 'YOUTUBE_VIDEO'), 'video'],
    ] as Array<[number, string]>;
    const out = parts.filter(([c]) => c > 0).map(([c, l]) => `${c} ${l}${c === 1 ? '' : 's'}`);
    return out.length ? out.join(' · ') : 'no assets yet';
  };

  if (error) return <GoogleAdsNotice error title="Could not load ad groups">{error}<Button size="sm" variant="ghost" onClick={load}>Retry</Button></GoogleAdsNotice>;
  if (groups === null) return <SkeletonRows count={2} indent="pl-9" />;

  return (
    <div className="space-y-1 pl-2">
      {groups.length === 0 && <div className="py-2 text-sm text-text-muted">No asset groups yet — the campaign can't serve without one.</div>}
      {groups.map((g) => {
        const open = openId === g.id;
        const st = statusOf(g.status, g.primaryStatus);
        const list = assets[g.id];
        const texts = (list ?? []).filter((a) => a.type === 'TEXT');
        const images = (list ?? []).filter((a) => a.type === 'IMAGE');
        const videos = (list ?? []).filter((a) => a.type === 'YOUTUBE_VIDEO');
        return (
          <div key={g.id} className={clsx('rounded-xl border transition-colors', open ? 'border-border-strong bg-surface' : 'border-border/70 bg-surface hover:border-border-strong')}>
            <div className="flex items-center gap-2 px-3 py-2">
              <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => setOpenId(open ? null : g.id)}>
                {open ? <ChevronDown size={16} className="shrink-0 text-text-muted" /> : <ChevronRight size={16} className="shrink-0 text-text-muted" />}
                <Layers size={16} className="shrink-0 text-text-muted/70" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-text-primary">{g.name}</div>
                  <div className="truncate text-xs text-text-muted">Asset group · {summarise(list)}{g.finalUrls[0] ? ` · ${g.finalUrls[0]}` : ''}</div>
                </div>
              </button>
              <Pill {...st} />
              <Switch checked={g.status === 'ENABLED'} disabled={campaign.advertisingChannelType === 'VIDEO' || busy === g.id || g.status === 'REMOVED'} label="Enable asset group" onCheckedChange={() => toggle(g)} />
            </div>
            {open && (
              <div className="space-y-3 border-t border-border/50 px-3.5 py-3">
                {list === undefined || list === null ? <SkeletonRows count={2} /> : list.length === 0 ? (
                  <div className="text-xs text-text-muted">No assets linked to this group.</div>
                ) : (
                  <>
                    {(['HEADLINE', 'LONG_HEADLINE', 'DESCRIPTION', 'BUSINESS_NAME'] as const).map((ft) => {
                      const rows = texts.filter((a) => a.fieldType === ft);
                      if (!rows.length) return null;
                      return (
                        <div key={ft}>
                          <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">{pretty(ft)}{rows.length > 1 ? ` · ${rows.length}` : ''}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {rows.map((a) => <span key={a.assetId} className="rounded-lg border border-border bg-surface px-2 py-1 text-xs text-text-primary">{a.text}</span>)}
                          </div>
                        </div>
                      );
                    })}
                    {images.length > 0 && (
                      <div>
                        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Images & logos · {images.length}</div>
                        <div className="flex flex-wrap gap-2">
                          {images.map((a) => (
                            <a key={a.assetId} href={a.imageUrl} target="_blank" rel="noreferrer" title={`${pretty(a.fieldType)}${a.width && a.height ? ` · ${a.width}×${a.height}` : ''}`}
                              className="group/img relative overflow-hidden rounded-lg border border-border bg-surface-alt">
                              {a.imageUrl ? <img src={a.imageUrl} alt="" className="h-16 w-auto max-w-[140px] object-cover" /> : <span className="flex h-16 w-24 items-center justify-center text-2xs text-text-muted">no preview</span>}
                              <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 py-0.5 text-[9px] leading-tight text-white opacity-0 transition-opacity group-hover/img:opacity-100">{pretty(a.fieldType)}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {videos.length > 0 && (
                      <div>
                        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Videos · {videos.length}</div>
                        <div className="flex flex-wrap gap-2">
                          {videos.map((a) => (
                            <a key={a.assetId} href={a.youtubeVideoId ? `https://youtu.be/${a.youtubeVideoId}` : undefined} target="_blank" rel="noreferrer" className="overflow-hidden rounded-lg border border-border" title={a.youtubeVideoTitle ?? a.youtubeVideoId}>
                              {a.youtubeVideoId ? <img src={`https://i.ytimg.com/vi/${a.youtubeVideoId}/mqdefault.jpg`} alt="" className="h-16 w-auto object-cover" /> : <span className="flex h-16 w-28 items-center justify-center text-2xs text-text-muted">video</span>}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DefaultConnectGate({ auth, accountUid, onChanged }: { auth: YtAuthStatus; accountUid: string; onChanged: () => void }) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!auth.connected) {
    return (
      <EmptyState
        icon={<Globe size={40} className="text-text-muted" />}
        title="No Google account connected"
        description="Connect Google in the Account page first."
        action={<Button onClick={() => navigate('/google-ads/account')} iconLeft={<Globe size={14} />}>Connect Google Ads</Button>}
      />
    );
  }
  return (
    <>
      <EmptyState
        icon={<Globe size={40} className="text-text-muted" />}
        title="Customer not attached yet"
        description="Pick which Google Ads customer this connection manages."
        action={<Button onClick={() => setPickerOpen(true)} iconLeft={<Plus size={14} />}>Choose account</Button>}
      />
      <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)}
        onAttached={() => { setPickerOpen(false); onChanged(); }} />
    </>
  );
}

// ── workspace ────────────────────────────────────────────────────────────

export default function GoogleAdsWorkspace({
  title, breadcrumb, description, embedded = false, filter, newCampaignRoute, emptyIcon: EmptyIcon, emptyHint,
  viewStorageKey, connectSlot, banner,
}: GoogleAdsWorkspaceProps) {
  const navigate = useNavigate();
  const { auth, account, accountUid, checkAuth } = useYoutubeAuth();
  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [leadFormIds, setLeadFormIds] = useState<Set<string>>(new Set());
  const [listError, setListError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [channel, setChannel] = useState('all');
  const [showFailed, setShowFailed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [view, setView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem(viewStorageKey) as 'list' | 'grid') || 'list'; } catch { return 'list'; }
  });
  const setViewPersisted = (v: 'list' | 'grid') => {
    setView(v);
    try { localStorage.setItem(viewStorageKey, v); } catch { /* storage unavailable */ }
  };
  const [player, setPlayer] = useState<{ id: string; title: string } | null>(null);
  const openPlayer = useCallback((id: string, t: string) => setPlayer({ id, title: t }), []);
  // Grid cards preview the campaign's first ad.
  const [previews, setPreviews] = useState<Record<string, GoogleAdsAdRow | null>>({});

  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const customerId = account?.customerId ?? '';
  const currency = auth?.currency ?? null;

  const load = useCallback(async () => {
    if (!ready) return;
    setListError(null);
    setRefreshing(true);
    try {
      const [list, leadForms] = await Promise.all([
        googleAdsService.listCampaigns(accountUid),
        // Only decides which Search builder "+ Ad" opens — never blocks the list.
        googleAdsService.search.leadFormCampaigns(accountUid).catch(() => [] as string[]),
      ]);
      setLeadFormIds(new Set(leadForms));
      setCampaigns(newestFirst(filter ? list.filter(filter) : list));
    } catch (e) {
      setListError(errMsg(e, 'Could not load campaigns.'));
      setCampaigns([]);
    } finally { setRefreshing(false); }
  }, [ready, accountUid, filter]);
  // customerId: re-attaching a different customer on the same account must refetch.
  useEffect(() => { load(); }, [load, customerId]);

  const live = useMemo(() => (campaigns ?? []).filter(isLive), [campaigns]);
  const [objective, setObjective] = useState<ObjectiveKey | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused'>('all');
  const [counts, setCounts] = useState<{ byCampaign: Record<string, { adGroups: number; ads: number; assetGroups?: number }>; byAdGroup: Record<string, number> } | null>(null);
  useEffect(() => {
    if (!ready || !accountUid) return;
    let cancelled = false;
    googleAdsService.campaignCounts(accountUid).then((c) => { if (!cancelled) setCounts(c); }).catch(() => { /* badges are optional */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, accountUid, campaigns]);
  const objectiveCounts = useMemo(() => {
    const m = new Map<ObjectiveKey, number>();
    for (const c of live) { const k = objectiveOf(c); m.set(k, (m.get(k) ?? 0) + 1); }
    return m;
  }, [live]);
  const visible = useMemo(() => live
    .filter((c) => objective === 'all' || objectiveOf(c) === objective)
    .filter((c) => statusFilter === 'all' || c.status === (statusFilter === 'active' ? 'ENABLED' : 'PAUSED'))
    .filter((c) => channel === 'all' || c.advertisingChannelType === channel)
    .filter((c) => `${c.name} ${c.id}`.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => sort === 'name' ? a.name.localeCompare(b.name) : sort === 'budget' ? Number(b.budgetAmountMicros || 0) - Number(a.budgetAmountMicros || 0) : 0),
  [live, objective, statusFilter, channel, query, sort]);
  const failed = useMemo(() => (campaigns ?? []).filter((c) => !isLive(c)), [campaigns]);

  // One ad per campaign, only for the grid — the list gets them on expand.
  useEffect(() => {
    if (view !== 'grid' || live.length === 0 || !accountUid) return;
    let alive = true;
    (async () => {
      try {
        const [groups, ads] = await Promise.all([
          googleAdsService.listAdGroups(accountUid),
          googleAdsService.listAds(accountUid),
        ]);
        const campaignOfGroup = new Map(groups.map((g) => [g.id, g.campaignId]));
        const entries = live.map((c) => [c.id, ads.find((a) => campaignOfGroup.get(a.adGroupId) === c.id) ?? null] as const);
        if (alive) setPreviews(Object.fromEntries(entries));
      } catch { /* previews are decoration — the grid still renders */ }
    })();
    return () => { alive = false; };
  }, [view, live, accountUid]);

  const toggle = async (c: GoogleAdsCampaignSummary) => {
    setBusy(c.id);
    try {
      if (c.status === 'ENABLED') await googleAdsService.pauseCampaign(accountUid, c.id);
      else await googleAdsService.resumeCampaign(accountUid, c.id);
      load();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  const expandFromGrid = (id: string) => { setViewPersisted('list'); setOpenId(id); };

  const toolbar = (
    <div className="gads-toolbar flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface p-3">
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        title="Switch Google Ads account"
        className="flex min-w-0 items-center gap-2 rounded-full border border-border bg-surface-alt px-3 py-1.5 text-sm text-text-primary transition-colors hover:border-primary/50"
      >
        <Globe size={14} className="shrink-0 text-primary" />
        <span className="max-w-[160px] truncate sm:max-w-[240px]">{auth?.customer_name || auth?.customer_id}</span>
      </button>
      <span className="flex-1" />
      <button type="button" onClick={() => load()} disabled={refreshing} title="Refresh" aria-label="Refresh"
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
        {refreshing ? <Spinner size={14} /> : <RefreshCw size={15} />}
      </button>
      <Button variant="ghost" size="sm" onClick={() => navigate('/google-ads/tools')} iconLeft={<BarChart3 size={15} />}>Reports</Button>
      <div className="flex h-9 overflow-hidden rounded-lg border border-border">
        <button type="button" onClick={() => setViewPersisted('list')} title="List view" aria-label="List view" aria-pressed={view === 'list'}
          className={clsx('px-2.5', view === 'list' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}>
          <List size={15} />
        </button>
        <button type="button" onClick={() => setViewPersisted('grid')} title="Grid view" aria-label="Grid view" aria-pressed={view === 'grid'}
          className={clsx('px-2.5', view === 'grid' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}>
          <LayoutGrid size={15} />
        </button>
      </div>
      <Button onClick={() => navigate(newCampaignRoute)} iconLeft={<Plus size={15} />} className="gads-primary h-9">
        <span className="hidden md:inline">New campaign</span><span className="md:hidden">New</span>
      </Button>
    </div>
  );

  const renderCampaignCard = (c: GoogleAdsCampaignSummary) => {
    const open = openId === c.id;
    const st = statusOf(c.status, c.primaryStatus);
    const { Icon, tile } = channelVisual(c.advertisingChannelType);
    const routes = childRoutes(c, leadFormIds);
    return (
      <Card key={c.id} padded={false} className="gads-campaign-card overflow-hidden rounded-2xl">
        <div className="gads-campaign-row">
          <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left"
            aria-expanded={open} onClick={() => setOpenId(open ? null : c.id)}>
            {open ? <ChevronDown size={18} className="shrink-0 text-text-muted" />
                  : <ChevronRight size={18} className="shrink-0 text-text-muted" />}
            <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tile)}>
              <Icon size={17} />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-[13px] font-semibold leading-tight text-text-primary">{c.name}</span>
                <ObjectivePill k={objectiveOf(c)} className="hidden sm:inline-flex" />
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
                <span className="truncate">{campaignSubline(c, currency)}</span>
                {open && <CampaignDates c={c} />}
                {counts?.byCampaign[c.id] && (
                  <span className="inline-flex items-center gap-2.5 text-xs">
                    {isPmax(c) ? (
                      <span className="inline-flex items-center gap-1"><CountGroups size={12} /> {counts.byCampaign[c.id].assetGroups ?? 0} asset group{(counts.byCampaign[c.id].assetGroups ?? 0) === 1 ? '' : 's'}</span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1"><CountGroups size={12} /> {counts.byCampaign[c.id].adGroups} ad group{counts.byCampaign[c.id].adGroups === 1 ? '' : 's'}</span>
                        <span className="inline-flex items-center gap-1"><CountAds size={12} /> {counts.byCampaign[c.id].ads} ad{counts.byCampaign[c.id].ads === 1 ? '' : 's'}</span>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          </button>
          <Pill {...st} />
          <Switch checked={c.status === 'ENABLED'} disabled={c.advertisingChannelType === 'VIDEO' || busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`}
            onCheckedChange={() => toggle(c)} />

        </div>
        {open && (
          <div className="gads-campaign-detail">
            {isPmax(c)
              ? <AssetGroupRows accountUid={accountUid} campaign={c} />
              : <AdGroupRows accountUid={accountUid} customerId={customerId} campaign={c} currency={currency}
                  addAd={routes.addAd} onPlay={openPlayer} adCounts={counts?.byAdGroup} onChanged={load} />}
            {routes.addChild && (
              <div className="pt-3 pl-9">
                <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                  onClick={() => navigate(routes.addChild!(c.id))}>
                  <Plus size={13} /> {isPmax(c) ? 'New asset group' : 'New ad group'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  const renderGridCard = (c: GoogleAdsCampaignSummary) => {
    const st = statusOf(c.status, c.primaryStatus);
    const { Icon, tile, label } = channelVisual(c.advertisingChannelType);
    const ad = previews[c.id];
    const vid = ad?.youtubeVideoId ?? null;
    return (
      <Card key={c.id} padded={false} className="flex flex-col overflow-hidden">
        <div className="relative">
          {vid
            ? <VideoThumb size="lg" videoId={vid} onPlay={() => ad && openPlayer(vid, adName(ad))} />
            : (
              <div className={clsx('flex aspect-video w-full items-center justify-center', tile)}>
                <Icon size={28} />
              </div>
            )}
          <span className="absolute right-2 top-2"><Pill {...st} /></span>
        </div>
        <div className="flex flex-1 flex-col p-3.5">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-text-primary">{c.name}</span>
            <ObjectivePill k={objectiveOf(c)} />
          </div>
          <div className="mt-0.5 truncate text-xs text-text-muted">
            {label}{ad ? ` · ${adName(ad)}` : ''}
          </div>
          <div className="mt-0.5 truncate text-xs text-text-muted">{campaignSubline(c, currency)}</div>
          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
            <Switch checked={c.status === 'ENABLED'} disabled={c.advertisingChannelType === 'VIDEO' || busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`}
              onCheckedChange={() => toggle(c)} />
            <span className="text-xs text-text-muted">{c.status === 'ENABLED' ? 'Enabled' : pretty(c.status)}</span>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => expandFromGrid(c.id)}>
              Open
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <>
      {!embedded && <Topbar title={title} breadcrumb={breadcrumb} description={description} />}

      <div className={clsx('gads-workspace w-full px-5', embedded ? 'pb-20 pt-3' : 'py-4')}>
        {banner}

        {auth === undefined && (
          <div className="space-y-3">
            <div className="skeleton h-14 rounded-2xl" />
            <SkeletonRows count={3} />
          </div>
        )}

        {auth !== undefined && !ready && (
          connectSlot
            ? connectSlot(auth ?? { connected: false }, accountUid, checkAuth)
            : <DefaultConnectGate auth={auth ?? { connected: false }} accountUid={accountUid} onChanged={checkAuth} />
        )}

        {ready && (
          <div className="w-full pb-10">
            {toolbar}
            <div className="gads-stats">
              {[
                { label: 'Total campaigns', value: live.length, note: 'Across your advertising channels', Icon: StatCampaigns },
                { label: 'Enabled', value: live.filter(c => c.status === 'ENABLED').length, note: 'Delivery depends on Google eligibility', Icon: StatActive },
                { label: 'Paused', value: live.filter(c => c.status === 'PAUSED').length, note: 'Ready when you are', Icon: StatPaused },
                { label: 'Channels', value: new Set(live.map(c => c.advertisingChannelType)).size, note: 'Your advertising mix', Icon: Globe },
              ].map(({ label, value, note, Icon }) => <div className="gads-stat" key={label}><span className="gads-stat-label">{label}<Icon size={17} /></span><span className="gads-stat-value">{campaigns === null || listError ? '—' : value}</span><span className="gads-stat-note">{note}</span></div>)}
            </div>
            <div className="gads-filterbar flex flex-wrap items-center gap-3">
              <label className="relative min-w-[200px] flex-1"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" /><Input aria-label="Search campaigns" placeholder="Search campaigns by name or ID…" className="pl-9" value={query} onChange={e => setQuery(e.target.value)} /></label>
              <Select aria-label="Campaign channel" value={channel} onChange={e => setChannel(e.target.value)} className="!w-auto"><option value="all">All channels</option>{Array.from(new Set(live.map(c => c.advertisingChannelType).filter(Boolean))).map(ch => <option value={ch} key={ch}>{channelVisual(ch).label}</option>)}</Select>
              <Select aria-label="Inferred campaign objective" title="Objective inferred from campaign type and bidding" value={objective} onChange={e => setObjective(e.target.value as ObjectiveKey | 'all')} className="!w-auto"><option value="all">All objectives</option>{(Object.keys(OBJECTIVES) as ObjectiveKey[]).filter(k => objectiveCounts.has(k)).map(k => <option key={k} value={k}>{OBJECTIVES[k].label}</option>)}</Select>
              <label className="flex items-center gap-2"><ArrowUpDown size={14} className="text-text-muted" /><Select aria-label="Sort campaigns" value={sort} onChange={e => setSort(e.target.value)} className="!w-auto"><option value="newest">Newest first</option><option value="name">Name A–Z</option><option value="budget">Daily budget</option></Select></label>
            </div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-text-muted">
              <span>Edit campaign settings or remove campaigns in Google Ads.</span>
              <a href="https://ads.google.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline">
                Open Google Ads <ExternalLink size={13} />
              </a>
            </div>
            <div className="mb-3 flex items-center justify-between"><div className="gads-status-chips"><button aria-pressed={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>All campaigns</button><button aria-pressed={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>Enabled</button><button aria-pressed={statusFilter === 'paused'} onClick={() => setStatusFilter('paused')}>Paused</button></div><h3 className="sr-only">Your campaigns <span className="ml-2 text-xs font-normal text-text-muted">{visible.length} shown</span></h3>{(query || channel !== 'all' || objective !== 'all' || statusFilter !== 'all') && <button className="text-xs text-info" onClick={() => { setQuery(''); setChannel('all'); setObjective('all'); setStatusFilter('all'); }}>Clear filters</button>}</div>
            <div className="min-w-0">
            <div className="space-y-3">

              {listError && (
                <Card role="alert" className="border-danger/30 bg-danger-soft p-4 text-sm text-danger">{listError}<button className="ml-3 underline" onClick={load}>Try again</button></Card>
              )}

              {campaigns === null && !listError && (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => <div key={i} className="skeleton h-[68px] rounded-2xl" />)}
                </div>
              )}

              {campaigns !== null && live.length === 0 && !listError && (
                <Card className="p-12 text-center">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                    <EmptyIcon size={22} />
                  </span>
                  <p className="text-sm font-medium text-text-primary">No campaigns yet</p>
                  <p className="mx-auto mt-1 max-w-xs text-xs text-text-muted">{emptyHint}</p>
                  <Button className="mt-5" onClick={() => navigate(newCampaignRoute)} iconLeft={<Plus size={15} />}>
                    New campaign
                  </Button>
                </Card>
              )}

              {view === 'grid' && live.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                  {visible.map(renderGridCard)}
                </div>
              )}

              {view === 'list' && visible.length > 0 && <div className="gads-campaign-list">{visible.map(renderCampaignCard)}</div>}
              {live.length > 0 && visible.length === 0 && (
                <Card className="p-8 text-center text-sm text-text-muted">No campaigns match your filters. Try another name, channel or status.</Card>
              )}

              {failed.length > 0 && (
                <Card padded={false} className="overflow-hidden border-warning/25">
                  <button type="button" className="flex w-full items-center gap-2.5 px-4 py-3 text-left"
                    onClick={() => setShowFailed((v) => !v)}>
                    <AlertTriangle size={15} className="shrink-0 text-warning" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-text-primary">
                        {failed.length} local draft{failed.length === 1 ? '' : 's'}
                      </div>
                      <div className="text-2xs text-text-muted">No Google campaign ID is saved. Check Google Ads before retrying.</div>
                    </div>
                    {showFailed ? <ChevronDown size={15} className="text-text-muted" />
                                : <ChevronRight size={15} className="text-text-muted" />}
                  </button>
                  {showFailed && (
                    <div className="border-t border-border/50 bg-surface-alt/30 px-4">
                      {failed.map((c, i) => (
                        <div key={c.id || i} className="flex items-start gap-3 border-t border-border/40 py-2.5 first:border-t-0">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[13px] text-text-muted">{c.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>


            </div>
          </div>
        )}
      </div>

      {ready && (
        <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)}
          onAttached={async () => { setPickerOpen(false); await checkAuth(); }} />
      )}

      {player && <VideoPlayer videoId={player.id} title={player.title} onClose={() => setPlayer(null)} />}
    </>
  );
}
